import React, { useState, useCallback, useMemo } from 'react';
import { KnightKey, AnvilUpgrade } from '../../../types';
import { capitalize } from '../../../utils/capitalize';
import { SWORN_DATA } from '../../../gameData';
import { Check } from 'lucide-react';
import { useSwornBuilderContext } from '../../../contexts/SwornBuilderContext';
import ConfirmModal from '../../modals/ConfirmModal';

const CompendiumAssetItem = React.memo(({ 
  itemKey, 
  data, 
  onSelect,
  selectLabel
}: { 
  itemKey: string; 
  data: { description: string; anvil_upgrades: AnvilUpgrade[] }; 
  onSelect: (e: React.MouseEvent) => void;
  selectLabel: string;
}) => {
  return (
    <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
      <div className="flex items-center gap-3 mb-2">
        <h4 className="text-lg font-medium text-white capitalize">
          {itemKey.split('_').join(' ')}
        </h4>
        <button 
          onClick={onSelect}
          className="px-2 py-0.5 text-[10px] font-bold bg-slate-700 text-sworn-gold border border-sworn-gold rounded hover:bg-slate-600 transition-colors"
        >
          {selectLabel}
        </button>
      </div>
      <p className="text-slate-300 text-sm mb-3">{data.description || 'No description available.'}</p>
      <div className="space-y-2">
        <h5 className="text-sm font-medium text-yellow-400">Anvil Upgrades:</h5>
        {data.anvil_upgrades.map((upgrade: AnvilUpgrade, index: number) => (
          <div key={index} className="text-xs text-slate-400 pl-2 border-l-2 border-yellow-600">
            <span className="font-medium text-yellow-300">{upgrade.name}:</span> {upgrade.effect}
          </div>
        ))}
      </div>
    </div>
  );
});

interface KnightCompendiumViewProps {
  selectedKnight: KnightKey;
}

const KnightCompendiumView: React.FC<KnightCompendiumViewProps> = ({ selectedKnight }) => {
  const { selectedKnight: activeKnight, setSelectedKnight, setSelectedWeapon, setSelectedSpell } = useSwornBuilderContext();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingWeapon, setPendingWeapon] = useState<{ key: string; knight: KnightKey } | null>(null);

  const handleWeaponClick = useCallback((e: React.MouseEvent, weaponKey: string) => {
    e.stopPropagation();
    
    if (selectedKnight && activeKnight && selectedKnight !== activeKnight) {
        setPendingWeapon({ key: weaponKey, knight: selectedKnight });
        setShowConfirmModal(true);
    } else {
        setSelectedWeapon(weaponKey);
    }
  }, [selectedKnight, activeKnight, setSelectedWeapon]);

  const confirmWeaponChange = useCallback(() => {
    if (pendingWeapon) {
        setSelectedKnight(pendingWeapon.knight);
        setSelectedWeapon(pendingWeapon.key);
        setShowConfirmModal(false);
        setPendingWeapon(null);
    }
  }, [pendingWeapon, setSelectedKnight, setSelectedWeapon]);

  const cancelWeaponChange = useCallback(() => {
    setShowConfirmModal(false);
    setPendingWeapon(null);
  }, []);

  const knightData = SWORN_DATA.classes[selectedKnight];
  const weapons = useMemo(() => Object.entries(knightData.weapons), [knightData]);
  const spells = useMemo(() => Object.entries(knightData.spells), [knightData]);

  return (
    <div className="h-full bg-slate-900/95 backdrop-blur-sm rounded-lg border border-sworn-brown p-4 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-2 border-b border-sworn-brown">
        <div className="flex items-center gap-3"> 
          <h2 className="text-2xl font-bold text-sworn-gold">{capitalize(selectedKnight)}</h2>
          {/* <div className="text-xs text-red-500 bg-black p-1 rounded border border-red-500">
              View: {selectedKnight} | Active: {activeKnight}
          </div> */}
          <button 
            onClick={() => setSelectedKnight(selectedKnight)}
            className="px-3 py-1 text-xs font-bold bg-sworn-gold text-black rounded hover:bg-sworn-gold-dark transition-colors border border-black shadow-sm"
          >
            Select Knight
          </button>
        </div>
      </div>

      {/* Knight Description */}
      <div className="mb-6">
        <p className="text-slate-300 leading-relaxed">{knightData.base_description}</p>
      </div>

      {/* Weapons Section */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-sworn-gold mb-3">Weapons</h3>
        <div className="space-y-4">
          {weapons.map(([weaponKey, weaponData]) => (
            <CompendiumAssetItem
              key={weaponKey}
              itemKey={weaponKey}
              data={weaponData}
              onSelect={(e) => handleWeaponClick(e, weaponKey)}
              selectLabel="Select Weapon"
            />
          ))}
        </div>
      </div>

      {/* Spells Section */}
      <div>
        <h3 className="text-xl font-semibold text-sworn-gold mb-3">Spells</h3>
        <div className="space-y-4">
          {spells.map(([spellKey, spellData]) => (
            <CompendiumAssetItem
              key={spellKey}
              itemKey={spellKey}
              data={spellData}
              onSelect={() => setSelectedSpell(spellKey)}
              selectLabel="Select Spell"
            />
          ))}
        </div>
      </div>

      <ConfirmModal
          isOpen={showConfirmModal}
          onClose={cancelWeaponChange}
          onConfirm={confirmWeaponChange}
          title="Change Knight?"
          message={`This weapon belongs to ${capitalize(pendingWeapon?.knight || '')}. Selecting it will change your active knight to ${capitalize(pendingWeapon?.knight || '')}. Do you want to proceed?`}
          confirmLabel="Change Knight"
          confirmIcon={<Check className="w-5 h-5" />}
          confirmButtonClass="bg-sworn-gold hover:bg-sworn-gold-dark text-black"
      />
    </div>
  );
};

export default KnightCompendiumView;
